import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TabbedCard } from "@ucl/library";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { useSetAtom } from "jotai";
import { financialApplicationIdAtomWithStorage } from "@/atoms/financialApplicationIdAtom";
import "./ConsumerPortalApplicationViewPage.scss";
import variables from "@/config/variables";
import { ApplicantSummary } from "@/components/ApplicantSummary/ApplicantSummary";
import { BoltTaskCompactGrid } from "@bolt/task";
import { useAuthHeadersRequired } from "@/lib/auth/authUtils";
import { RequireAuth } from "@/lib/auth/RequireAuth";
import { JointAccountOwnerExpressionSchemaTaskExecutionTypeForm } from "@/components/TaskExecutionTypeForms/JointAccountOwnerExpressionSchemaTaskExecutionTypeForm";
import { DocumentTemplateTaskExecutionTypeModule } from "@bolt/documenttemplates";
import { KeyPersonExpressionSchemaTaskExecutionTypeForm } from "@/components/TaskExecutionTypeForms/KeyPersonExpressionSchemaTaskExecutionTypeForm";
import { FinancialApplicationExpressionSchemaTaskExecutionTypeForm } from "@/components/TaskExecutionTypeForms/FinancialApplicationExpressionSchemaTaskExecutionTypeForm";

export const ConsumerPortalApplicationViewPage: React.FC = () => {
  return (
    <RequireAuth clientType="consumer">
      <ConsumerPortalApplicationViewPageContent />
    </RequireAuth>
  );
};

const ConsumerPortalApplicationViewPageContent: React.FC = () => {
  const { financialApplicationId } = useParams<{ financialApplicationId: string }>();
  const navigate = useNavigate();
  const getAuthRequestHeader = useAuthHeadersRequired();
  const setFinancialApplicationId = useSetAtom(financialApplicationIdAtomWithStorage);

  // Set the financial application ID in the atom when the component mounts
  useEffect(() => {
    if (financialApplicationId) {
      setFinancialApplicationId(financialApplicationId);
    }
  }, [financialApplicationId, setFinancialApplicationId]);

  const handleBackToApplications = () => {
    navigate("/customer/portal/applications");
  };

  const tabsData = [
    {
      tabKey: "summary",
      headerContent: "Application Summary",
      tabContent: financialApplicationId ? (
        <div className="consumer-portal-application-view__tab-content">
          <ApplicantSummary financialApplicationId={financialApplicationId} />
        </div>
      ) : (
        <div>Loading...</div>
      ),
    },
    {
      tabKey: "tasks",
      headerContent: "Tasks",
      tabContent: (
        <div className="consumer-portal-application-view__tab-content">
          <BoltTaskCompactGrid
            className="consumer-portal-application-tasks-grid"
            apiBaseUrl={variables.apiBaseUrl}
            gridKey="consumer-application-tasks-grid"
            getAuthRequestHeader={getAuthRequestHeader}
            isTaskOverviewReadOnly={true}
            formRegistry={{
              FinancialApplicationExpressionSchema: {
                FormComponent: FinancialApplicationExpressionSchemaTaskExecutionTypeForm,
              },
              JointAccountOwnerExpressionSchema: {
                FormComponent: JointAccountOwnerExpressionSchemaTaskExecutionTypeForm,
              },
              KeyPersonExpressionSchema: {
                FormComponent: KeyPersonExpressionSchemaTaskExecutionTypeForm,
              },
            }}
            taskExecutionModuleRegistry={{
              DocumentTemplateTaskExecutionType: DocumentTemplateTaskExecutionTypeModule,
            }}
            taskExecutionLeftPanelIsDocked={true}
            taskExecutionRightPanelIsDocked={false}
            commentPermissions={{
              canComment: true,
              canEdit: false,
              canPin: false,
              canDelete: false,
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="consumer-portal-application-view">
      <div className="consumer-portal-application-view__header">
        <div className="consumer-portal-application-view__breadcrumb">
          <button
            className="consumer-portal-application-view__back-link"
            onClick={handleBackToApplications}
          >
            <FontAwesomeIcon
              icon={faArrowLeft}
              className="consumer-portal-application-view__back-icon"
            />
            My Applications
          </button>
          <span className="consumer-portal-application-view__breadcrumb-separator">&gt;</span>
          <span className="consumer-portal-application-view__current-application">
            Application #{financialApplicationId?.slice(0, 8)}
          </span>
        </div>
      </div>

      <div className="consumer-portal-application-view__content">
        <TabbedCard
          tabs={tabsData}
          className="consumer-portal-application-view__tabbed-card"
          basePath={"/customer/portal/applications/" + financialApplicationId}
        />
      </div>
    </div>
  );
};

export default ConsumerPortalApplicationViewPage;
